/**
 * PortalAoVivo — o ao vivo do portal do parceiro (D3, passo 19 — PR 2). Renderiza `null`; mora no `PortalLayout` (sob `PortalAuthProvider`
 * + `ProtectedPortalRoute`: só monta com parceiro autenticado e desmonta no redirecionamento SPA — o `App.tsx`, arquivo-espinha, não é tocado).
 *
 * O que liga:
 *  - o socket (`/api/v1/portal/ws/notifications`) pelo MESMO transporte do interno, sem reserva SSE (o SSE é 403 para o parceiro), com o token
 *    conferido/renovado a cada tentativa e o fechamento 4001/4003 decidido por `decisaoAoFechar` (`socketDoPortal.ts`);
 *  - a presença por aba (`{"type":"aba","visivel":…}`) — sem ela o push sai com o portal aberto;
 *  - o avisador do D1 (`useMontagemDoAviso`: 1º gesto, trava do som, título) e o tratador dos 4 eventos (`aoVivoDoPortal.ts`);
 *  - a REFEITURA do que se perde: o Redis não guarda nada, então ao (re)entrar em `ws` e quando a aba volta à vista refaz o sino, as não lidas, a
 *    lista de sessões e a busca da conversa aberta. O servidor marca `read_at` em toda busca; a feita com a aba escondida vai com
 *    `marcar_lidas=false` (`paramsDeLeitura`, PR 1 Decisão 10) e a de quando a aba volta vai sem — é essa que "abre" a conversa.
 * 🔴 Nada daqui toca a API nem o contexto internos (um 401 interno apagaria o `seweb_auth` de uma sessão interna no mesmo navegador).
 * ⚠️ É o ÚNICO montador do transporte do portal: o modo do `realtime-store` (que o sino e o contador leem para pausar o polling) só é escrito
 *    por ele. Sem `getAuthToken` e sem `sseUrl`: o transporte só usa o token no SSE, e o do portal não tem SSE (o token vai na URL do WS).
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";

import type { AcoesDoAviso } from "@/components/notifications/AvisoNaTela";
import { mostrarAvisoDoPortal } from "@/components/portal/avisoDoPortal";
import { usePortalAuth } from "@/contexts/PortalAuthContext";
import { useMontagemDoAviso } from "@/hooks/useMontagemDoAviso";
import { invalidarAtendimentoDoPortal } from "@/hooks/usePortalAtendimento";
import { invalidarNotificacoesDoPortal } from "@/hooks/usePortalNotifications";
import { usePortalPermissions } from "@/hooks/usePortalPermissions";
import { usePresencaDoPortal } from "@/hooks/usePresencaDoPortal";
import { useRealtimeNotifications } from "@/hooks/useRealtimeNotifications";
import { salaAberta } from "@/lib/avisos/salaAberta";
import { tratarEventoDoPortal, type ContextoDoAoVivo } from "@/lib/portal/aoVivoDoPortal";
import { CHAVE_DAS_NAO_LIDAS_DO_ATENDIMENTO } from "@/lib/portal/naoLidasDoAtendimento";
import { decisaoAoFechar, urlDoWsDoPortal } from "@/lib/portal/socketDoPortal";
import { portalNotificationsApi } from "@/services/portalApi";

/** Refaz o que um socket caído ou uma aba escondida pode ter perdido. */
function refazerTudo(qc: QueryClient, podeAtendimento: boolean): void {
  invalidarNotificacoesDoPortal(qc);
  if (!podeAtendimento) return;
  void qc.invalidateQueries({ queryKey: CHAVE_DAS_NAO_LIDAS_DO_ATENDIMENTO });
  // A conversa aberta é buscada de novo. Aba escondida: a busca vai com `marcar_lidas=false` e não marca lida; aba à vista: marca e zera.
  invalidarAtendimentoDoPortal(qc, salaAberta());
}

export function PortalAoVivo(): null {
  const qc = useQueryClient();
  const { user } = usePortalAuth();
  const { has } = usePortalPermissions();
  const podeAtendimento = has("portal:atendimento:view");

  useMontagemDoAviso();

  const vistosRef = useRef<Set<string>>(new Set());
  const acoes = useMemo<AcoesDoAviso>(
    () => ({
      // "Lida" do aviso: marca pela API do PORTAL e refaz o sino (o aviso de atendimento nunca chega aqui: não é linha do sino).
      marcarLida: (id) => {
        void portalNotificationsApi
          .markRead(id)
          .then(() => invalidarNotificacoesDoPortal(qc))
          .catch(() => undefined);
      },
    }),
    [qc]
  );

  const contextoRef = useRef<ContextoDoAoVivo | null>(null);
  contextoRef.current = {
    qc,
    userId: user?.id ?? null,
    podeAtendimento,
    mostrarAviso: mostrarAvisoDoPortal,
    acoes,
    vistos: vistosRef.current,
  };
  const aoEvento = useCallback((evento: unknown) => {
    if (contextoRef.current) tratarEventoDoPortal(evento, contextoRef.current);
  }, []);

  const { mode, enviar } = useRealtimeNotifications({
    wsUrl: urlDoWsDoPortal,
    onEvent: aoEvento,
    aoFechar: decisaoAoFechar,
    enabled: !!user,
  });

  usePresencaDoPortal(enviar, mode === "ws");

  // (Re)entrou em `ws`: o que chegou durante a queda não volta (o pub/sub do Redis não guarda nada).
  const modoAnterior = useRef(mode);
  useEffect(() => {
    const antes = modoAnterior.current;
    modoAnterior.current = mode;
    if (mode !== "ws" || antes === "ws") return;
    refazerTudo(qc, podeAtendimento);
  }, [mode, qc, podeAtendimento]);

  // A aba voltou a ficar visível (ou o computador acordou): o socket pode ter ficado mudo e a resposta pode ter chegado escondida.
  useEffect(() => {
    const aoVoltar = () => {
      if (document.visibilityState === "visible") refazerTudo(qc, podeAtendimento);
    };
    document.addEventListener("visibilitychange", aoVoltar);
    return () => document.removeEventListener("visibilitychange", aoVoltar);
  }, [qc, podeAtendimento]);

  return null;
}
